<template>
  <div v-show="isOpen" class="fixed inset-0 z-50 flex items-center justify-center px-3 bg-blackly/50 backdrop-blur-xs">
    <div
      ref="countryBarContainer"
      class="bg-white relative max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden"
      data-testid="countryBar"
      role="dialog"
      aria-modal="true"
      aria-labelledby="country-bar-title"
      :aria-busy="!isInitialized"
      tabindex="-1"
    >
      <button
        v-if="!isLoading"
        ref="closeButton"
        data-testid="btn-country-bar-close"
        class="hover:opacity-80 absolute top-0 right-0 flex items-center justify-center p-4 text-blackly ring-inset"
        @click="close"
      >
        <IconClose class="w-4" />
        <span class="sr-only">{{ t('localeBar.close') }}</span>
      </button>

      <div class="px-6 pt-8 pb-16 overflow-y-auto">
        <h2 id="country-bar-title" class="mb-4 font-semibold fluid-2xl leading-tightest text-blackly">
          {{ t('localeBar.title') }}
        </h2>

        <div v-if="!isInitialized" class="flex justify-center items-center py-32 lg:py-48" aria-live="polite">
          <SpinnerLarge class="w-20" />
          <span class="sr-only">{{ t('localeBar.loading') }}</span>
        </div>

        <form v-else id="countrySelect" @submit.prevent="close" aria-live="polite">
          <div v-for="group in regions" :key="group.name" class="mb-5">
            <div v-if="group.name" class="text-3xl font-base mt-5 mb-2">{{ group.name }}</div>

            <div class="text-copy sm:mr-0 text-blackly mb-4" v-html="t(group.description)"></div>

            <div class="grid sm:grid-cols-3 gap-2">
              <button
                v-for="region in group.stores"
                :key="region.code"
                type="button"
                @click="selectCountry(region.code)"
                @keypress.enter="selectCountry(region.code, true)"
                :class="[
                  'flex items-center py-1 px-3 cursor-pointer border-2 border-transparent transition-colors text-left',
                  iso === region.code ? 'bg-br-green-light ring-3 ring-black' : '',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-br-green',
                ]"
                :aria-pressed="iso === region.code ? 'true' : 'false'"
              >
                <LocationIcon class="w-5 h-6 mr-2 text-blackly" :class="{ 'text-br-green': iso === region.code }" />
                <div>
                  <div class="text-lg font-bold">{{ region.country }}</div>
                  <div v-if="region.description" class="text-xs">{{ region.description }}</div>
                  <div>{{ region.lang }}</div>
                </div>
              </button>
            </div>
          </div>

          <button type="submit" class="hidden">{{ t('localeBar.save') }}</button>
        </form>
      </div>

      <div class="absolute bottom-0 inset-x-0 bg-white/90 backdrop-blur-xs flex px-6 py-3">
        <CtaButton ref="saveButton" type="button" color="outline" :loading="isLoading" @click.native="close">
          {{ t('localeBar.save') }}
        </CtaButton>
      </div>
    </div>
  </div>
</template>

<script>
import IconClose from '@/components/layout/header/IconClose';
import CtaButton from '@/components/common/CtaButton';
import LocationIcon from '@/components/icons/LocationIcon';
import { Bus } from '@/utils/bus';
import { setPreferredLangCookie } from '@/utils/cookie';
import { stripLocale } from '@/utils/i18n';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useFocusTrapManager } from '@/utils/focusTrapManager';
import SpinnerLarge from '@/components/SpinnerLarge.vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { getAddToCartParamsFromUrl } from '@/utils/product';
import { useMainNavStore } from '@/stores/main-nav';

export default defineNuxtComponent({
  components: { CtaButton, IconClose, LocationIcon, SpinnerLarge },

  data() {
    return {
      isOpen: false,
      isLoading: false,
      isInitialized: false,
      alternates: {},

      iso: 'ch-de',

      regions: [
        {
          name: null,
          description: 'localeBar.chooseCountry',
          stores: [
            { code: 'ch-de', lang: 'Deutsch', country: 'Schweiz' },
            { code: 'ch-fr', lang: 'Français', country: 'Suisse' },
          ],
        },
      ],
    };
  },

  setup() {
    const { t, locale } = useI18n({ useScope: 'local' });
    const countryBarContainer = ref(null);
    const route = useRoute();
    const mainNavStore = useMainNavStore();

    const { activate, deactivate } = useFocusTrap(countryBarContainer);
    const focusTrapManager = useFocusTrapManager();

    return {
      t,
      locale,
      route,
      countryBarContainer,
      activateFocusTrap: activate,
      deactivateFocusTrap: deactivate,
      focusTrapManager,
      mainNavStore,
    };
  },

  computed: {
    availableLanguages() {
      return this.regions.flatMap((region) => region.stores.map((store) => store.code));
    },

    fallbackPath() {
      return `/${this.iso}/`;
    },
  },

  watch: {
    isOpen(newValue) {
      if (newValue) {
        // hide OneTrust banner while country bar is open
        if (window && window.hideOneTrustBanner) {
          window.hideOneTrustBanner();
        }

        this.$nextTick(() => {
          // Registriere CountryBar Focus Trap mit mittlerer Priorität
          if (this.focusTrapManager) {
            this.focusTrapManager.register(
              'countryBar',
              {
                activate: this.activateFocusTrap,
                deactivate: this.deactivateFocusTrap,
              },
              50
            );

            // Aktiviere CountryBar Trap (wird OneTrust deaktivieren falls aktiv)
            this.focusTrapManager.activate('countryBar');
          } else {
            // Fallback falls FocusTrapManager nicht verfügbar
            this.activateFocusTrap();
          }
        });
      } else {
        // remove onetrust style when closing
        if (window && window.showOneTrustBanner) {
          window.showOneTrustBanner();
        }

        // Deaktiviere und entferne CountryBar Trap
        if (this.focusTrapManager) {
          this.focusTrapManager.deactivate('countryBar');
          this.focusTrapManager.unregister('countryBar');
        } else {
          this.deactivateFocusTrap();
        }
      }
    },
  },

  async mounted() {
    try {
      // load alternates once the component is mounted
      this.initAlternates();

      // check if the user is on a page that requires a language selection
      const storedIso = window.localStorage.getItem('preferredIso');

      // ignore selections of locales that are no longer available, so the user picks again
      if (storedIso && this.availableLanguages.includes(storedIso)) {
        // in this case the user has already selected a language
        // we just set it to the right value
        this.iso = storedIso;
      } else if (!this.$route.path.includes('/account/')) {
        const shouldAutoSelect = this.shouldAutoSelectCountry();
        if (shouldAutoSelect) {
          // If add-to-cart params are present, infer locale from URL and persist selection
          const urlLocale = this.extractLocaleFromPath(this.$route.path);
          this.iso = urlLocale;
          this.locale = urlLocale;
          setPreferredLangCookie(urlLocale);
          window.localStorage.setItem('preferredIso', urlLocale);
        } else {
          this.isOpen = true;
          await this.fetchCountryIso();
        }
      }
    } catch (error) {
      // set to current locale if there is an error
      this.iso = this.locale;
    } finally {
      // after running this block, the component is initialized
      this.isInitialized = true;
    }

    document.addEventListener('keydown', this.handleEscKey);

    Bus.on('openCountryBar', () => {
      this.isLoading = false;
      this.isOpen = true;

      // update alternates in case they have changed
      this.initAlternates();
    });
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscKey);
  },

  methods: {
    handleEscKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    },

    shouldAutoSelectCountry() {
      // Skip opening when add-to-cart parameters are present in URL
      return !!getAddToCartParamsFromUrl();
    },

    extractLocaleFromPath(path) {
      const pathSegments = path.split('/').filter((segment) => segment.length > 0);
      if (pathSegments.length > 0) {
        const firstSegment = pathSegments[0];
        if (this.availableLanguages.includes(firstSegment)) {
          return firstSegment;
        }
      }
      return 'ch-de';
    },

    initAlternates() {
      if (this.route.name?.startsWith('account')) {
        let path = stripLocale(this.route.path);

        // Ensure path doesn't start with a slash to avoid double slashes
        if (path.startsWith('/')) {
          path = path.substring(1);
        }

        // we need to generate the alternates for the account page
        this.alternates = {
          'ch-de': '/ch-de/' + path,
          'ch-fr': '/ch-fr/' + path,
        };
      } else {
        this.alternates = this.mainNavStore.alternates;
      }
    },

    selectCountry(code, closeOnConfirm = false) {
      if (closeOnConfirm && this.iso === code) {
        this.close();
      }

      this.iso = code;
      this.locale = code;
    },

    async fetchCountryIso() {
      try {
        return await $fetch('/api/country', {
          transformRequest: (data, headers) => {
            delete headers.Authorization;
            delete headers.common.Authorization;
            return data;
          },
        })
          .then((data) => {
            return data?.iso?.toLowerCase();
          })
          .then((iso) => {
            switch (iso) {
              case 'ch':
                return 'ch-de';

              default:
                return iso;
            }
          })
          .then((iso) => {
            if (!this.availableLanguages.includes(iso)) {
              throw new Error('Language not available');
            }

            this.isOpen = true;
            this.iso = iso;
            this.locale = this.iso;
          });
      } catch (e) {
        this.isOpen = true;
        return null;
      }
    },

    close() {
      // save settings to localstorage & cookie
      setPreferredLangCookie(this.iso);
      window.localStorage.setItem('preferredIso', this.iso);

      // Show OneTrust banner now that language is selected
      if (typeof window !== 'undefined' && window.OneTrust) {
        // Remove the alert box closed cookie to allow banner to show
        document.cookie = 'OptanonAlertBoxClosed=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

        // Call reloadOTBanner to show the banner
        if (window.reloadOTBanner) {
          window.reloadOTBanner();
        }
      }

      // just close if nothing changed
      if (this.$i18n.locale === this.iso) {
        this.isOpen = false;

        // Nach dem Schließen der CountryBar, aktiviere OneTrust falls Banner sichtbar
        if (this.focusTrapManager && this.focusTrapManager.isOneTrustBannerVisible()) {
          setTimeout(() => {
            this.focusTrapManager.activate('oneTrust');
          }, 100);
        }

        return;
      }

      this.isLoading = true;

      // Preserve query parameters when redirecting
      const queryString = window.location.search;
      const targetUrl = this.alternates[this.iso] ? this.alternates[this.iso] : this.fallbackPath;

      window.location = targetUrl + queryString;
    },
  },
});
</script>

<i18n lang="json">
{
  "en": {
    "localeBar": {
      "chooseCountry": "<p>We believe you are in <b>Europe</b>.<br>If you are in a different location, select it to shop in the right online store & see content relevant to your location</p>",
      "save": "Save",
      "title": "Choose your location",
      "close": "Close country selection dialog",
      "loading": "Loading location options..."
    }
  },
  "de": {
    "localeBar": {
      "chooseCountry": "<p>Wir glauben, dass du in <b>Deutschland</b> bist.<br>Falls du an einem anderen Standort befindest, wähle diesen aus, um im richtigen Online-Shop einzukaufen & für deinen Standort relevante Inhalte zu sehen.</p>",
      "save": "Speichern",
      "title": "Wähle deinen Standort",
      "close": "Dialog zur Länderauswahl schließen",
      "loading": "Standortoptionen werden geladen..."
    }
  },
  "fr": {
    "localeBar": {
      "chooseCountry": "<p>Vous semblez être en <b>France</b>.<br>Si ce n'est pas le cas, sélectionnez votre pays pour accéder à la boutique et au contenu adaptés.</p>",
      "save": "Sauvegarder",
      "title": "Choisissez votre emplacement",
      "close": "Fermer la boîte de dialogue de sélection du pays",
      "loading": "Chargement des options de localisation..."
    }
  },
  "nl": {
    "localeBar": {
      "chooseCountry": "<p>We denken dat je in <b>Nederland</b> bent.<br>Als je je op een andere locatie bevindt, selecteer deze dan om in de juiste online winkel te winkelen & inhoud te zien die relevant is voor jouw locatie.</p>",
      "save": "Opslaan",
      "title": "Kies je locatie",
      "close": "Dialoogvenster voor landenselectie sluiten",
      "loading": "Locatieopties laden..."
    }
  },
  "ch-fr": {
    "localeBar": {
      "chooseCountry": "<p>Vous semblez être en <b>Suisse</b>.<br>Si ce n'est pas le cas, sélectionnez votre pays pour accéder à la boutique et au contenu adaptés.</p>",
      "save": "Sauvegarder",
      "title": "Choisissez votre emplacement",
      "close": "Fermer la boîte de dialogue de sélection du pays",
      "loading": "Chargement des options de localisation..."
    }
  },
  "ch-de": {
    "localeBar": {
      "chooseCountry": "<p>Wir glauben, dass du in der <b>Schweiz</b> bist.<br>Falls du an einem anderen Standort befindest, wähle diesen aus, um im richtigen Online-Shop einzukaufen & für deinen Standort relevante Inhalte zu sehen.</p>",
      "save": "Speichern",
      "title": "Wähle deinen Standort",
      "close": "Dialog zur Länderauswahl schließen",
      "loading": "Standortoptionen werden geladen..."
    }
  }
}
</i18n>
